import { RadioButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { Select } from "@nova/component-select"
import { ChecklistSubmissionWorkspaceVals } from "@nova/constants"
import { Input, TextArea } from "@nova/ds-component-input"
import { ChecklistAnswerSpec, ChecklistQuestionSpec, QuestionType } from "@nova/schemas"
import { FunctionComponent, memo, useMemo } from "react"

export interface AnswerControls {
    updateAnswer: <T extends QuestionType>(id: string, spec: ChecklistAnswerSpec<T>) => void
}

interface ChecklistInputBuilderProps {
    questionSpec: ChecklistQuestionSpec
    answerSpec: ChecklistAnswerSpec
    answerControls: AnswerControls
    workspaceVals: ChecklistSubmissionWorkspaceVals
}

const InputBody: FunctionComponent<ChecklistInputBuilderProps> = ({
    answerControls,
    questionSpec,
    answerSpec,
    workspaceVals,
}) => {
    if (questionSpec.type !== answerSpec.type) {
        return null
    }

    switch (questionSpec.type) {
        case "text":
            return (
                <ChecklistTextInput
                    questionSpec={questionSpec}
                    answerSpec={answerSpec as ChecklistAnswerSpec<"text">}
                    controls={answerControls}
                    isLong={questionSpec.variant === "long"}
                />
            )
        case "multi-select":
            return (
                <ChecklistMutliSelectInput
                    questionSpec={questionSpec}
                    answerSpec={answerSpec as ChecklistAnswerSpec<"multi-select">}
                    controls={answerControls}
                />
            )
        case "header":
            return null
        case "radio":
            return (
                <ChecklistRadioInput
                    questionSpec={questionSpec}
                    answerSpec={answerSpec as ChecklistAnswerSpec<"radio">}
                    controls={answerControls}
                />
            )
        case "user-select":
            return (
                <ChecklistUserInput
                    questionSpec={questionSpec}
                    answerSpec={answerSpec as ChecklistAnswerSpec<"user-select">}
                    controls={answerControls}
                    workspaceVals={workspaceVals}
                />
            )
        case "aircraft-select":
            return (
                <ChecklistAircraftInput
                    questionSpec={questionSpec}
                    answerSpec={answerSpec as ChecklistAnswerSpec<"aircraft-select">}
                    controls={answerControls}
                    workspaceVals={workspaceVals}
                />
            )
    }

    return null
}

export const ChecklistSubmissionInputBuilder: FunctionComponent<ChecklistInputBuilderProps> = memo(
    ({ questionSpec, answerControls, answerSpec, workspaceVals }) => {
        return (
            <div className="flex flex-col gap-2">
                <h3 className="text-white">
                    {questionSpec.question}
                    {"isRequired" in questionSpec && questionSpec.isRequired && "*"}
                </h3>
                <div className="text-gray-50">
                    <InputBody
                        questionSpec={questionSpec}
                        answerSpec={answerSpec}
                        answerControls={answerControls}
                        workspaceVals={workspaceVals}
                    />
                </div>
            </div>
        )
    }
)

ChecklistSubmissionInputBuilder.displayName = "ChecklistSubmissionInputBuilder"

interface ChecklistInputProps<T extends QuestionType> {
    answerSpec: ChecklistAnswerSpec<T>
    questionSpec: ChecklistQuestionSpec<T>
    controls: AnswerControls
}

const ChecklistTextInput: FunctionComponent<ChecklistInputProps<"text"> & { isLong: boolean }> = ({
    answerSpec,
    controls,
    questionSpec,
    isLong,
}) => {
    return isLong ? (
        <TextArea
            value={answerSpec.answer}
            onChange={(e) =>
                controls.updateAnswer(questionSpec.id, {
                    ...answerSpec,
                    answer: e.currentTarget.value,
                })
            }
        />
    ) : (
        <Input
            value={answerSpec.answer}
            onChange={(e) =>
                controls.updateAnswer(questionSpec.id, {
                    ...answerSpec,
                    answer: e.currentTarget.value,
                })
            }
        />
    )
}

const ChecklistMutliSelectInput: FunctionComponent<ChecklistInputProps<"multi-select">> = ({
    answerSpec,
    controls,
    questionSpec,
}) => {
    const handleAnswerChange = (index: number) => {
        const newAnswer = [...answerSpec.answer]
        newAnswer[index] = !newAnswer[index]
        controls.updateAnswer(questionSpec.id, { ...answerSpec, answer: newAnswer })
    }

    return (
        <div className="flex flex-col gap-2 pt-2">
            {questionSpec.options.map((option, i) => (
                <div
                    className="flex items-center gap-2 cursor-pointer h-12 lg:h-auto"
                    key={option}
                    onClick={() => handleAnswerChange(i)}
                >
                    <TriCheckbox state={answerSpec.answer[i] ? "checked" : "unchecked"} />
                    <h5 className="select-none">{option}</h5>
                </div>
            ))}
        </div>
    )
}

const ChecklistRadioInput: FunctionComponent<ChecklistInputProps<"radio">> = ({
    answerSpec,
    controls,
    questionSpec,
}) => {
    const handleAnswerChange = (index: number) => {
        const newAnswer = new Array(questionSpec.options.length).fill(false)
        newAnswer[index] = true
        controls.updateAnswer(questionSpec.id, { ...answerSpec, answer: newAnswer })
    }

    return (
        <div className="flex flex-col gap-2 pt-2">
            {questionSpec.options.map((option, i) => (
                <div
                    className="flex items-center gap-2 cursor-pointer h-12 lg:h-auto"
                    key={option}
                    onClick={() => handleAnswerChange(i)}
                >
                    <RadioButton isSelected={answerSpec.answer[i]} onClick={() => {}} />
                    <h5 className="select-none">{option}</h5>
                </div>
            ))}
        </div>
    )
}

const ChecklistUserInput: FunctionComponent<
    ChecklistInputProps<"user-select"> & { workspaceVals: ChecklistSubmissionWorkspaceVals }
> = ({ answerSpec, controls, workspaceVals }) => {
    const options = useMemo(() => {
        return [...workspaceVals.workspaceMembers]
            .sort(
                (a, b) =>
                    Number(b.isActive) - Number(a.isActive) || a.fullName.localeCompare(b.fullName)
            )
            .map((m) => {
                return {
                    label: m.fullName,
                    value: m.id,
                }
            })
    }, [workspaceVals.workspaceMembers])
    return (
        <Select<string>
            onChange={(v) =>
                controls.updateAnswer(answerSpec.questionId, {
                    ...answerSpec,
                    answer: v,
                })
            }
            options={options}
            value={answerSpec.answer}
            searchable
        />
    )
}

const ChecklistAircraftInput: FunctionComponent<
    ChecklistInputProps<"aircraft-select"> & { workspaceVals: ChecklistSubmissionWorkspaceVals }
> = ({ answerSpec, controls, workspaceVals }) => {
    const options = useMemo(() => {
        return workspaceVals.workspaceAircrafts.map((a) => {
            return {
                label: a.nickname ? `${a.nickname} (${a.model})` : (a.model ?? a.id),
                value: a.id,
            }
        })
    }, [workspaceVals.workspaceAircrafts])
    return (
        <Select<string>
            onChange={(v) =>
                controls.updateAnswer(answerSpec.questionId, {
                    ...answerSpec,
                    answer: v,
                })
            }
            options={options}
            value={answerSpec.answer}
            searchable
        />
    )
}
